import React, { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, Download } from 'lucide-react';
import { api } from '../lib/api.js';
import { addDays, isoDate } from '../lib/time.js';
import { validIso } from '../lib/dateInput.js';
import { useSessionState } from '../lib/useSessionState.js';
import { exportTrace } from '../lib/exportExcel.js';
import {
  traceContext,
  traceItemLabel,
  traceOrigin,
  traceSummary,
} from '../lib/trace.js';
import { PageHeader } from '../components/PageSkeleton.jsx';

const ACTION_LABEL = {
  create: 'Created',
  update: 'Updated',
  delete: 'Deleted',
  start: 'Started',
  pause: 'Paused',
  resume: 'Resumed',
  stop: 'Stopped',
  discard: 'Discarded',
};
const ACTION_CLASS = {
  create: 'trace-create',
  update: 'trace-update',
  delete: 'trace-delete',
  start: 'trace-create',
  pause: 'trace-update',
  resume: 'trace-update',
  stop: 'trace-update',
  discard: 'trace-delete',
};

const FIELD_LABEL = {
  projectCode: 'code',
  projectName: 'project',
  workDate: 'date',
  start: 'start',
  end: 'end',
  description: 'description',
};

// Normalise any ISO datetime string to a plain date ("2026-05-22T00:00:00.000Z" → "2026-05-22")
function fmtVal(val) {
  if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(val))
    return val.slice(0, 10);
  return String(val ?? '');
}

function fmtTs(ts) {
  return new Date(ts).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

// Where a mutation came from — the MCP server tags every row it writes;
// anything else was a GUI-driven API call. Shown the same way regardless of
// entity type, so it's never lost inside a summary branch (trace.js#traceOrigin).
function OriginBadge({ details }) {
  const origin = traceOrigin(details);
  return (
    <span
      className="trace-origin"
      data-tooltip={
        origin.client ? `via MCP · ${origin.client}` : 'via the web app'
      }
    >
      {origin.label}
    </span>
  );
}

function EntryDetail({ log }) {
  const { details, action, entityType } = log;
  const d = details || {};

  // Time entries: the before/after diff, or the project + date + time line.
  if (entityType === 'time_entry') {
    if (action === 'update') {
      const { before, after } = d;
      const changed = Object.keys(after || {}).filter(
        (k) => fmtVal(before?.[k]) !== fmtVal(after?.[k]),
      );
      return (
        <div className="trace-detail">
          <OriginBadge details={d} />
          <span className="trace-owner">{d.entryOwnerName}</span>
          <span className="trace-sep">·</span>
          {changed.map((k) => (
            <span key={k} className="trace-change">
              <span className="trace-field">{FIELD_LABEL[k] || k}</span>
              <span className="trace-before">{fmtVal(before?.[k])}</span>
              <span className="trace-arrow">→</span>
              <span className="trace-after">{fmtVal(after?.[k])}</span>
            </span>
          ))}
        </div>
      );
    }
    return (
      <div className="trace-detail">
        <OriginBadge details={d} />
        <span className="trace-owner">{d.entryOwnerName}</span>
        <span className="trace-sep">·</span>
        <span className="trace-entry-info">
          {d.projectCode && <strong>{d.projectCode}</strong>}
          {d.projectName && <span> {d.projectName}</span>}
          {d.workDate && <span> · {fmtVal(d.workDate)}</span>}
          {d.start && d.end && (
            <span>
              {' '}
              · {d.start}–{d.end}
            </span>
          )}
          {d.description && (
            <span className="trace-desc"> · {d.description}</span>
          )}
        </span>
      </div>
    );
  }

  // Intelligence edits: the project / client it touched + a short summary.
  const context = traceContext(d);
  const summary = traceSummary(entityType, d);
  return (
    <div className="trace-detail">
      <OriginBadge details={d} />
      {context ? (
        <span className="trace-entry-info">
          <strong>{context}</strong>
        </span>
      ) : (
        <span className="muted-text">—</span>
      )}
      {summary && (
        <>
          <span className="trace-sep">·</span>
          <span className="trace-desc">{summary}</span>
        </>
      )}
    </div>
  );
}

export function Trace() {
  const [date, setDate] = useSessionState(
    'trace.date',
    isoDate(new Date()),
    (value, fallback) =>
      validIso(value) && value <= isoDate(new Date()) ? value : fallback,
  );
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    api(`/audit-log?date=${date}`)
      .then((data) => {
        if (active) setLogs(data);
      })
      .catch(() => {
        if (active) setLogs([]);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [date]);

  function prev() {
    setDate(isoDate(addDays(new Date(date + 'T12:00:00'), -1)));
  }
  function next() {
    setDate(isoDate(addDays(new Date(date + 'T12:00:00'), 1)));
  }
  const isToday = date === isoDate(new Date());

  return (
    <div>
      <PageHeader
        title="Trace"
        subtitle={`Audit log · ${loading ? 'Loading…' : `${logs.length} event${logs.length !== 1 ? 's' : ''}`}`}
        actions={
          <button
            className="secondary"
            onClick={() => exportTrace(logs, date)}
            disabled={logs.length === 0}
          >
            <Download size={14} /> Export
          </button>
        }
      />

      <div className="trace-nav">
        <button
          className="icon-button"
          onClick={prev}
          aria-label="Previous day"
          data-tooltip="Previous day"
        >
          <ChevronLeft size={16} />
        </button>
        <span className="trace-date">{date}</span>
        <button
          className="icon-button"
          onClick={next}
          disabled={isToday}
          aria-label="Next day"
          data-tooltip="Next day"
        >
          <ChevronRight size={16} />
        </button>
      </div>

      {!loading && logs.length === 0 && (
        <div className="trace-empty">No activity recorded for this day.</div>
      )}

      {logs.length > 0 && (
        <div className="trace-table-wrap">
          <table className="trace-table">
            <thead>
              <tr>
                <th>Time</th>
                <th>Actor</th>
                <th>Action</th>
                <th>Item</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log) => (
                <tr key={log.id}>
                  <td className="trace-ts">{fmtTs(log.ts)}</td>
                  <td className="trace-actor">{log.actorName}</td>
                  <td>
                    <span
                      className={`trace-badge ${ACTION_CLASS[log.action] || ''}`}
                    >
                      {ACTION_LABEL[log.action] || log.action}
                    </span>
                  </td>
                  <td className="trace-item">
                    {traceItemLabel(log.entityType)}
                  </td>
                  <td>
                    <EntryDetail log={log} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
